// ============================================================================
// PWAUpdateNotice — banner que aparece quando há nova versão do app disponível
// ============================================================================
// Usa o virtual:pwa-register pra detectar quando o SW baixou uma nova versão.
// Quando detecta, mostra um banner fixo embaixo com botão "Atualizar agora".
// ============================================================================

import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import {
  whenReloadAllowed, isReloadHeld, decidirRecarga, marcarVersaoPendente, quandoSoltarSemTeto, iniciarRecarga, isReloadHeldSemTeto, recargaEmCurso,
} from '../utils/reloadHold.js';
import { CRM_SEM_RECARGA } from '../config/featureFlags.js';

export default function PWAUpdateNotice() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [updateSW, setUpdateSW] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Import dinâmico — evita break no SSR/tests onde virtual: não existe
    import('virtual:pwa-register').then(({ registerSW }) => {
      if (cancelled) return;
      const update = registerSW({
        immediate: true,
        onNeedRefresh() {
          // Auto-update: aplica imediatamente sem esperar clique do usuário.
          // Mostra o banner por 1.5s pra dar feedback visual e então recarrega.
          // Isso resolve o problema de PWA mobile/iOS onde usuário nem vê o
          // banner e fica com versão antiga indefinidamente.
          // Antes, espera a trava do ponto (batida em andamento): no máximo 60 s.
          whenReloadAllowed().then(async () => {
            // 27/10/2026 (CRM_SEM_RECARGA): com o CRM segurando, guarda a versão pendente e espera soltar;
            // um vigia só recarrega (o VersionChecker pode estar recarregando junto)
            if (CRM_SEM_RECARGA) {
              if (decidirRecarga({ ligado: true }) === 'adiar') { marcarVersaoPendente('sw'); await quandoSoltarSemTeto(); }
              if (!iniciarRecarga()) return;
            }
            if (!cancelled) setNeedRefresh(true);
            setTimeout(() => {
              try { update(true); } catch { window.location.reload(); }
            }, 1500);
          });
        },
        onRegisteredSW(_swUrl, registration) {
          // Verifica updates a cada 30s — captura mudanças mesmo com app sempre aberto
          if (registration) {
            // Com registerType 'autoUpdate' o SW novo recarrega a página sozinho
            // ao ativar: não começa uma checagem no meio de uma batida de ponto
            // (a próxima checagem, 30 s depois, pega a versão nova).
            const checar = () => {
              if (isReloadHeld()) return;
              // CRM com texto, foco ou envio: o SW novo recarregaria sozinho ao ativar
              if (CRM_SEM_RECARGA && (isReloadHeldSemTeto() || recargaEmCurso())) return;
              registration.update().catch(() => {});
            };
            setInterval(checar, 30_000);
            // Verifica também quando a aba volta a ficar visível
            document.addEventListener('visibilitychange', () => {
              if (document.visibilityState === 'visible') checar();
            });
          }
        },
      });
      setUpdateSW(() => update);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!needRefresh || dismissed) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[360px] z-[100] bg-[#0F1B2D] border border-brand-300/30 rounded-2xl shadow-2xl p-4 animate-slide-up"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-brand-300/15 flex items-center justify-center flex-shrink-0">
          <RefreshCw size={16} className="text-brand-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-[#E9EEF5]">Atualizando…</h4>
          <p className="text-xs text-[#8C9CB5] mt-0.5">Carregando a versão mais recente.</p>
        </div>
      </div>
    </div>
  );
}
